import React, { useState, useEffect } from 'react';
import { func, bool, number, object, string } from 'prop-types';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, useForm } from '@laramie/ui';
import { useFormErrors } from '../../hooks/useFormErrors';
import { APIS } from '../../../config/apis';
import { ServerRequest } from '../../../utils/apiweb';
import { InputCustom, Loading, FormLabel, Modal, FormError } from '../../common';
import { isValidString,isNull,isValidDate,isValidNumber } from '../../../utils/validator';
import { GetInitDato, GetTitleFromField } from '../../../utils/helpers';
import {TIPS} from "../../../consts/tips";
import {VALIDATION_MESSAGE} from "../../../consts/validationMessage";

const EntidadModal = (props) => {

    //variables
    const entityInit = {
        id: 0,
        codigo: '',
        nombre: '',
        orden: 0
    };

    //hooks
    const [state, setState] = useState({
        loading: false,
        entity: entityInit
    });

    // se arman en el primer render: si llegaran después, el registro se pediría
    // dos veces y la respuesta sin los campos propios podía pisar a la otra
    const [fields] = useState(() => {
        let fields = [];
        for (let i=1; i<=10; i++) {
            const nombre = props.definition[`nombre${i}`];
            if (isValidString(nombre, true)) {
                const tipoDato = props.definition[`tipoDato${i}`];
                const descripcion = props.definition[`descripcion${i}`];
                const obligatorio = props.definition[`obligatorio${i}`];
                const field = {
                    nombre: nombre,
                    descripcion: descripcion,
                    tipoDato: tipoDato,
                    obligatorio: obligatorio,
                    valor: GetInitDato(tipoDato)
                }
                fields.push(field);
            }
        }
        return fields;
    });

    const [ formValues, formHandle, , formSet ] = useForm({
        codigo: '',
        nombre: '',
        orden: ''
    });

    useEffect(() => {

        if (props.id > 0) {
            FindEntidad();
        }
        else {
            let newFormValues = {
                codigo: '',
                nombre: '',
                descripcion: '',
                obligatorio: false,
                orden: ''
            };
            fields.forEach(field => {
                newFormValues[field.nombre] = field.valor;
            });
            formSet(newFormValues);
        }

    }, [fields]);

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors);

    //handles
    const handleClickAceptar = () => {
        if (validate()) {
            if (props.id === 0) {
                AddEntidad();
            }
            else {
                ModifyEntidad();
            }
        };
    };
    const handleClickCancelar = () => {
        props.onDismiss();
    }
    
    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
            const message = error.message;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
                return {...prevState, loading: false};
            });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
            return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }  

    // funciones
    function getErrors() {

        let errors = {};

        if (!isValidString(formValues.codigo, true)) errors.codigo = VALIDATION_MESSAGE.REQUIRED;
        if (!isValidString(formValues.nombre, true)) errors.nombre = VALIDATION_MESSAGE.REQUIRED;
        if (isEmptyValue(formValues.orden)) errors.orden = VALIDATION_MESSAGE.REQUIRED;

        for (const field of fields) {
            if (field.obligatorio) {
                const error = getFieldError(field);
                if (error) errors[field.nombre] = error;
            }
        }

        return errors;
    }

    function getFormatErrors() {

        let errors = {};

        if (!isEmptyValue(formValues.orden) && !isValidNumber(formValues.orden, true)) {
            errors.orden = 'Ingresá un número mayor a cero';
        }

        return errors;
    }

    function isEmptyValue(valor) {
        return isNull(valor) || valor.toString().length === 0;
    }

    function getFieldError(field) {

        const valor = formValues[field.nombre];

        switch(field.tipoDato) {
            case "number":
            case "decimal":
                return !isEmptyValue(valor) ? null : VALIDATION_MESSAGE.REQUIRED;
            case "list":
            case "entity":
                return isValidNumber(valor, true) ? null : VALIDATION_MESSAGE.REQUIRED_OPTION;
            case "date":
                return isValidDate(valor, true) ? null : VALIDATION_MESSAGE.REQUIRED_DATE;
            case "boolean":
                return null;
            default:
                return isValidString(valor, true) ? null : VALIDATION_MESSAGE.REQUIRED;
        }
    }

    function FindEntidad() {
    
        setState(prevState => {
          return {...prevState, loading: true};
        });
    
        const callbackSuccess = (response) => {
            response.json()
            .then((data) => {

                fields.forEach(field => {
                    if (field.nombre.startsWith('fecha')) {
                        if (data[field.nombre] !== null) data[field.nombre] = new Date(data[field.nombre]);
                    }
                });

                setState(prevState => {
                  return {...prevState, loading: false, entity: data};
                });

                let newFormValues = {
                    codigo: data.codigo,
                    nombre: data.nombre,
                    orden: data.orden
                };
                fields.forEach(field => {
                    newFormValues[field.nombre] = data[field.nombre];
                });
                formSet(newFormValues);
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                  return {...prevState, loading: false};
                });
            });
        };
    
        const paramsUrl = `/${props.definition.tipo}/${props.id}`;
    
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.ENTIDAD,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    
    }
    
    function AddEntidad() {
        const method = REQUEST_METHOD.POST;
        const paramsUrl = `/${props.definition.tipo}`;
        SaveEntidad(method, paramsUrl);
    }
    
    function ModifyEntidad() {
        const method = REQUEST_METHOD.PUT;
        const paramsUrl = `/${props.definition.tipo}/${props.id}`;
        SaveEntidad(method, paramsUrl);
    }    

    function SaveEntidad(method, paramsUrl) {

        setState(prevState => {
          return {...prevState, loading: true};
        });
    
        const callbackSuccess = (response) => {
          response.json()
          .then((row) => {
            setState(prevState => {
                return {...prevState, loading: false};
            });
            props.onConfirm(row.id);
          })
          .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
          });
        };
    
        const dataBody = {
            ...state.entity,
            codigo: formValues.codigo,
            nombre: formValues.nombre,
            orden: parseInt(formValues.orden)
        };
        fields.forEach(field => {
            dataBody[field.nombre] = formValues[field.nombre];
        });
    
        ServerRequest(
            method,
            null,
            true,
            APIS.URLS.ENTIDAD,
            paramsUrl,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    
    }

  return (
    <>

    <Loading visible={state.loading}></Loading>

    <Modal
      size="lg"
      title={`${props.definition.descripcion}: ${(props && props.id > 0) ? state.entity.nombre : "Nuevo"}`}
      tipName={props.tipName || TIPS.NAME.MODALS.ENTIDAD_MODAL}
      onEscape={() => handleClickCancelar()}
      body={(<>

            <div className="row">

                <div className="col-12 col-md-6 col-lg-4">
                    <FormLabel htmlFor="codigo" required>Código</FormLabel>
                    <input
                        name="codigo"
                        type="text"
                        placeholder=""
                        className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                        value={ formValues.codigo }
                        onChange={ formHandle }
                        maxLength={50}
                        disabled={props.disabled}
                    />
                    <FormError message={errorOf('codigo')} />
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                    <FormLabel htmlFor="orden" required>Orden</FormLabel>
                    <input
                        name="orden"
                        type="number"
                        placeholder=""
                        className={`form-control ${errorOf('orden') ? 'is-invalid' : ''}`}
                        value={ formValues.orden }
                        onChange={ formHandle }
                        disabled={props.disabled}
                    />
                    <FormError message={errorOf('orden')} />
                </div>
                <div className="col-12">
                    <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                    <input
                        name="nombre"
                        type="text"
                        placeholder=""
                        className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                        value={ formValues.nombre }
                        onChange={ formHandle }
                        maxLength={250}
                        disabled={props.disabled}
                    />
                    <FormError message={errorOf('nombre')} />
                </div>

            </div>

            <div className="row">
                {fields.map(field => (
                    <div className="col-12 col-md-6" key={field.nombre}>
                        <FormLabel htmlFor={field.nombre} required={field.obligatorio}>{field.descripcion}</FormLabel>
                        <InputCustom
                            name={field.nombre}
                            type={field.tipoDato}
                            placeholder=""
                            className="form-control"
                            value={ formValues[field.nombre] }
                            onChange={ formHandle }
                            disabled={props.disabled}
                            serialize={false}
                            entidad={(field.tipoDato === "entity") ? field.nombre.substring(2) : ""}
                            lista={(field.tipoDato === "list") ? field.nombre.substring(2) : ""}
                            invalid={!!errorOf(field.nombre)}
                        />
                        <FormError message={errorOf(field.nombre)} />
                    </div>
                )
            )}

            </div>

      </>)}
      footer={(<>
            {!props.disabled &&
            <>
              <button className="btn btn-outline-primary" data-dismiss="modal" onClick={ (event) => handleClickCancelar() }>Cancelar</button>
              <button className="btn btn-primary" data-dismiss="modal" onClick={ (event) => handleClickAceptar() }>Aceptar</button>
            </>
            }

            {props.disabled &&
            <div className="width-100p f-align">
              <button className="btn back-button" data-dismiss="modal" onClick={ (event) => handleClickCancelar() }>Volver</button>
            </div>
            }
      </>)}
    />

    </>
  );
}

EntidadModal.propTypes = {
    disabled: bool,
    id: number.isRequired,
    definition: object.isRequired,
    onConfirm: func.isRequired,
    onDismiss: func.isRequired,
    tipName: string,
};

EntidadModal.defaultProps = {
    disabled: false,
    tipName: ''
};

export default EntidadModal;